import {
    ContentAdditionalProductFragment,
    EventCtaFragment,
    MediaFragment,
    RedirectionType,
    StoryToPlayFragment,
} from '@graphql/generated/graphql';
import { TStory, TStoryPreview } from '@hello-pimster/pimster-ui';
import { EventCta } from '@lib/pimster-stories/components/CtaHandler';
import { SanitizedStoryPage } from '@lib/pimster-stories/types';
import { getMediaType } from '@lib/pimster-stories/utils/getMediaType';
import { isSomething } from '@utils/isSomething';
import { parseVideo } from '@utils/video';

import { toImage } from './image';
import { getStoryPageLinks, resolveLinkUrl } from './link';
import {
    BuildContext,
    getStoryPageAdditionalProducts,
    toShoppable,
} from './shoppable';

export const getStoryPreviewVideo = (
    story?: StoryToPlayFragment
): MediaFragment | undefined => {
    const firstStoryPage = story?.StoryPages?.[0];
    const media = firstStoryPage?.Media;
    if (media && getMediaType(media) === 'video') return media;
};

export const toStoryPreview = (
    story: StoryToPlayFragment,
    context: BuildContext & { withTitle?: boolean }
): TStoryPreview => {
    const video = getStoryPreviewVideo(story);
    const shoppables = getStoryPageAdditionalProducts(
        story.StoryPages?.[0] ?? undefined
    );
    return {
        id: story.id,
        slug: story.Slug || '',
        image: toImage(
            story.PreviewImage,
            undefined,
            story.PreviewImage?.alternativeText || ''
        ),
        ...(video && {
            video: {
                sources: parseVideo(video).sources,
            },
        }),
        title: context.withTitle ? story.Title : undefined,
        shoppables: shoppables.map((s) => toShoppable(s, context)),
    };
};

const getShoppablesFromCtas = (
    ctas?: SanitizedStoryPage['ctas']
): (ContentAdditionalProductFragment['Additional_product'] & {
    selectedVariant?: ContentAdditionalProductFragment['Selected_variant'];
})[] =>
    (ctas || [])
        .map((cta) =>
            cta.__typename === 'ComponentContentAdditionalProduct' &&
            cta.Additional_product
                ? {
                      ...cta.Additional_product,
                      selectedVariant: cta.Selected_variant,
                  }
                : undefined
        )
        .filter(isSomething);

export const toStory = (
    story: StoryToPlayFragment,
    storyPages: SanitizedStoryPage[],
    context: BuildContext
): TStory => {
    return {
        id: story.id,
        slug: story.Slug || '',
        title: context.companyConfig?.story.withTitle ? story.Title : undefined,
        previewImage: toImage(
            story.PreviewImage,
            undefined,
            story.PreviewImage?.alternativeText || ''
        ),
        pages: (storyPages || []).map((sp) => {
            const shoppables = getShoppablesFromCtas(sp.ctas);
            const links = getStoryPageLinks(sp)
                .filter((l) => l.Redirection_Type === RedirectionType.Outside)
                .map((l) => ({
                    href: resolveLinkUrl(l, context),
                    label: l.Text,
                }));
            const eventCtas = sp.ctas?.filter(
                (cta): cta is EventCtaFragment =>
                    cta.__typename === 'ComponentCtaEventCallToAction'
            );
            const video = parseVideo(sp.media);

            return {
                id: sp.id,
                slug: sp.slug,
                title: sp.title,
                subtitle: sp.subtitle,
                loop: sp.looping,
                media:
                    getMediaType(sp.media) === 'video'
                        ? {
                              type: 'video' as const,
                              ...video,
                          }
                        : {
                              type: 'image' as const,
                              ...toImage(
                                  sp.media,
                                  undefined,
                                  sp.media.alternativeText || ''
                              ),
                          },
                shoppables: shoppables.map((s) => toShoppable(s, context)),
                links,
                children: eventCtas?.map((cta) => (
                    <EventCta key={cta.id} cta={cta} story={story} />
                )),
            };
        }),
    };
};
